// CRM lists and saved views (step 3.12, job R-01; docs/CRM_TOOLS.md "crm_list_records"). Companies and People page
// on the server through crm_list_records, 50 rows at a time with the true total, and keep views in saved_views
// (act save_view and delete_view). A list's spec is Ask's own (lib/ask.ts) plus the search box and fixed filters.
import type { Crm } from "./crm";
import type { AskColumn, AskFilter, AskSort } from "./ask";
import type { FlowSearch } from "./engineFlow";

export type RecordSource = "firms" | "people";
export const PAGE = 50;
/** The most rows a "Download CSV" reads, 200 at a time. */
export const DOWNLOAD_MAX = 10_000;

/** The search box and the page's own filters. Firms: q, module and flow. People: all but flow. */
export interface RecordSearch {
  q?: string;
  module?: string;
  /** Firms only: the firms behind a block or band of the engine flow (crm_engine_flow_firms). */
  flow?: FlowSearch;
  tags?: string[];
  organisation?: string;
  source?: string;
  introduced_by?: string;
  channel?: string;
}

export interface RecordSpec {
  source: RecordSource;
  filters: AskFilter[];
  sort?: AskSort[];
  columns?: string[];
  search?: RecordSearch;
}

/** Where the last page stopped: its sort value, its id, and whether the rows with no value had started. */
export interface RecordCursor {
  v: unknown;
  id: string;
  n: boolean;
}

export interface RecordPage {
  source: RecordSource;
  rows: Record<string, unknown>[];
  total: number;
  columns: AskColumn[];
  sort: AskSort;
  next_cursor: RecordCursor | null;
}

export interface SavedView {
  id: string;
  source: RecordSource;
  name: string;
  spec: RecordSpec;
  shared: boolean;
  mine: boolean;
  created_by: string;
  updated_at: string;
}

/** The search part without its empty values (so an empty box is no search at all, and views compare cleanly). */
export function cleanSearch(s: RecordSearch | undefined): RecordSearch | undefined {
  if (!s) return undefined;
  const out: RecordSearch = {};
  if (s.q?.trim()) out.q = s.q.trim();
  if (s.module) out.module = s.module;
  if (s.tags?.length) out.tags = [...s.tags];
  if (s.organisation) out.organisation = s.organisation;
  if (s.source) out.source = s.source;
  if (s.introduced_by) out.introduced_by = s.introduced_by;
  if (s.channel) out.channel = s.channel;
  if (s.flow) out.flow = { ...s.flow };
  return Object.keys(out).length ? out : undefined;
}

/** Columns the browser works out itself (such as the module) start with "_"; the server leaves them out. */
export const isVirtual = (c: string) => c.startsWith("_");

/** What goes to the server for a page: the browser's own columns are left out. */
export function serverSpec(spec: RecordSpec): RecordSpec {
  const columns = spec.columns?.filter((c) => !isVirtual(c));
  const search = cleanSearch(spec.search);
  return {
    source: spec.source,
    filters: spec.filters,
    ...(spec.sort?.length ? { sort: spec.sort } : {}),
    ...(columns?.length ? { columns } : {}),
    ...(search ? { search } : {}),
  };
}

/** A stable key for a spec, to tell whether a view has changed and when to load again. */
export function specKey(spec: RecordSpec): string {
  const s = serverSpec(spec);
  return JSON.stringify([s.source, s.filters, s.sort ?? null, spec.columns ?? null, s.search ?? null]);
}

export async function listRecords(crm: Crm, spec: RecordSpec, cursor: RecordCursor | null = null, limit = PAGE): Promise<RecordPage> {
  const r = await crm.read<RecordPage | null>("crm_list_records", { p_spec: serverSpec(spec), p_cursor: cursor, p_limit: limit });
  return {
    source: spec.source,
    rows: r?.rows ?? [],
    total: Number(r?.total ?? 0),
    columns: r?.columns ?? [],
    sort: r?.sort ?? { field: "id", dir: "asc" },
    next_cursor: r?.next_cursor ?? null,
  };
}

/** Every row of a list (for a CSV), 200 at a time, up to `max`. */
export async function allRecords(crm: Crm, spec: RecordSpec, max = DOWNLOAD_MAX): Promise<RecordPage> {
  const first = await listRecords(crm, spec, null, 200);
  const rows = [...first.rows];
  let cursor = first.next_cursor;
  while (cursor && rows.length < max) {
    const next = await listRecords(crm, spec, cursor, 200);
    rows.push(...next.rows);
    cursor = next.next_cursor;
  }
  return { ...first, rows: rows.slice(0, max), next_cursor: null };
}

export function listViews(crm: Crm, source: RecordSource): Promise<SavedView[]> {
  return crm.read<SavedView[] | null>("crm_list_views", { p_source: source }).then((v) => v ?? []);
}

export function saveView(crm: Crm, v: { source: RecordSource; name: string; spec: RecordSpec; shared: boolean; view?: string | null }): Promise<SavedView> {
  const name = v.name.trim();
  if (!name) return Promise.reject(new Error("Give the view a name."));
  const s = serverSpec(v.spec);
  return crm.write<SavedView>("save_view", {
    source: v.source, name, shared: v.shared,
    spec: { filters: s.filters, ...(s.sort ? { sort: s.sort } : {}), ...(v.spec.columns?.length ? { columns: v.spec.columns } : {}), ...(s.search ? { search: s.search } : {}) },
    ...(v.view ? { view: v.view } : {}),
  });
}

export function deleteView(crm: Crm, id: string): Promise<boolean> {
  return crm.write<boolean>("delete_view", { view: id });
}

/** A saved view's spec as a list's spec (its columns, filters, sort and search). */
export function viewSpec(v: SavedView, base: RecordSpec): RecordSpec {
  return {
    source: base.source,
    filters: Array.isArray(v.spec?.filters) ? v.spec.filters : [],
    sort: Array.isArray(v.spec?.sort) ? v.spec.sort : base.sort,
    columns: Array.isArray(v.spec?.columns) && v.spec.columns.length ? v.spec.columns : base.columns,
    search: v.spec?.search ?? undefined,
  };
}

// ---------------------------------------------------------------------------- plain words for the Companies list (J-05)
const STEP_ID = /^\d{1,2}\.(\d{1,2}[A-Z]?|R|W|D)$/;

/** Where a firm came from, in plain words with no step IDs ("Ogma's database", "Companies House", "Added by hand"). */
const SOURCE_WORDS: [RegExp, string][] = [
  [/^(2\.(16|17|18|19|20|21|34|38)|ogma_ref|ogma|ref|prospect_database|first_value)$/, "Ogma's database"],
  [/^(2\.[23]|companies_house.*)$/, "Companies House"],
  [/^(2\.1|register.*)$/, "Public registers"],
  [/^(0\.4[56]|by_hand|manual|owner|paste|added_by_hand)$/, "Added by hand"],
  [/^(0\.4[0-2]|import|csv|upload|hubspot|pipedrive|salesforce|crm_import)$/, "Imported"],
  [/^(0\.47|mailbox.*|inbox|google_calendar|microsoft_calendar|calendar)$/, "Your mailbox and calendar"],
  [/^(1\.\d+|inbound|form|website|page|enquiry|chat|social_dm|call_back)$/, "Came to you"],
  [/^(2\.[78]|3\.11|referral|introduction|introduced|intro)$/, "An introduction"],
  [/^(2\.4|2\.35|signal|job_post|news)$/, "News and job posts"],
  [/^(2\.(5|6)|deal_platform|broker)$/, "Deal platforms"],
  [/^(2\.(9|10)|tender.*)$/, "Tenders"],
  [/^(2\.13|maps?|google_maps|places)$/, "Map listings"],
  [/^(2\.14|director(y|ies))$/, "Directories"],
  [/^(2\.15|social)$/, "Social profiles"],
  [/^(person_move|10\.17)$/, "A contact moved there"],
  [/^(event|events|5\.\d+)$/, "An event"],
];

export function sourceLabel(source: string | null | undefined): string {
  const s = String(source ?? "").trim();
  if (!s) return "Not recorded";
  const hit = SOURCE_WORDS.find(([re]) => re.test(s.toLowerCase()));
  if (hit) return hit[1];
  if (STEP_ID.test(s)) return "Found by Ogma";
  const words = s.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const CHANNEL_WORDS: Record<string, string> = {
  email: "Email", instagram: "Instagram", facebook: "Facebook", linkedin: "LinkedIn", phone: "Call", call: "Call", letter: "Letter",
  whatsapp: "WhatsApp", sms: "Text", video: "Video", tiktok: "TikTok",
};

/** "Email", "Instagram", "Call": a touch's channel as a word. */
export function channelLabel(channel: string | null | undefined): string {
  const c = String(channel ?? "").toLowerCase();
  if (!c) return "";
  return CHANNEL_WORDS[c] ?? c.charAt(0).toUpperCase() + c.slice(1).replace(/_/g, " ");
}

/** Each firm's latest sent message (its channel and when), from its touches. */
export function lastMessages(touches: Record<string, unknown>[]): Record<string, { channel: string; at: string }> {
  const out: Record<string, { channel: string; at: string }> = {};
  for (const t of touches) {
    const org = t.organisation_id ? String(t.organisation_id) : "";
    if (!org || !t.sent_at) continue;
    if (t.status && !["sent", "delivered", "imported"].includes(String(t.status))) continue;
    const at = String(t.sent_at);
    if (!out[org] || out[org]!.at < at) out[org] = { channel: String(t.channel ?? ""), at };
  }
  return out;
}
